import {
  getDeviceZoomCenter,
  getCameraScroll,
  getDeviceZoomLevels,
  getMinimumCoverZoom,
  getViewThresholds,
} from "@/helpers/masterplan/zoomViewport";

describe("masterplan zoom viewport", () => {
  it("raises the zoom when the image is too short for the map viewport", () => {
    expect(getMinimumCoverZoom(900, 700, 1600, 900)).toBeCloseTo(1.382716, 5);
    expect(getMinimumCoverZoom(900, 400, 1600, 900)).toBe(1);
  });

  it("uses the mobile expanded image base width when it already covers height", () => {
    expect(getMinimumCoverZoom(390, 844, 1600, 900, 1501)).toBe(1);
  });

  it("shows stages immediately when a map has no estate boundary", () => {
    const thresholds = getViewThresholds({
      minimumZoom: 1.3,
      estateZoom: 1.8,
      stageZoom: 2,
      lotZoom: 2.5,
      hasEstateBoundary: false,
      stageDefaultOffset: 0.18,
      lotDefaultOffset: 0.42,
    });

    expect(thresholds.stages).toBe(1.3);
    expect(thresholds.lots).toBe(2.5);
  });

  it("keeps estate, stage, and lot views distinct after an old zoom is clamped", () => {
    const thresholds = getViewThresholds({
      minimumZoom: 1.3,
      estateZoom: 0.8,
      stageZoom: 1.1,
      lotZoom: 1.2,
      hasEstateBoundary: true,
      stageDefaultOffset: 0.18,
      lotDefaultOffset: 0.42,
    });

    expect(thresholds.estate).toBeCloseTo(1.3);
    expect(thresholds.stages).toBeCloseTo(1.4);
    expect(thresholds.lots).toBeCloseTo(1.5);
  });
});

describe("device zoom settings", () => {
  const desktop = { estateBoundary: 1, stages: 2, lots: 3 };
  const mobile = { estateBoundary: 1.4, stages: 1.6, lots: 1.8 };

  it("keeps desktop and mobile settings independent", () => {
    const settings = { ...desktop, mobile };

    expect(getDeviceZoomLevels(settings, false)?.lots).toBe(3);
    expect(getDeviceZoomLevels(settings, true)).toEqual(mobile);
  });
  it("uses legacy settings on mobile until mobile settings are saved", () => {
    expect(getDeviceZoomLevels(desktop, true)).toEqual(desktop);
    expect(getDeviceZoomLevels(undefined, true)).toBeUndefined();
  });
});

it("selects independent landscape zooms and falls back for older maps", () => {
  const desktop = { estateBoundary: 1, stages: 2, lots: 3 };
  const mobile = { estateBoundary: 1.4, stages: 1.6, lots: 1.8 };
  const mobileLandscape = { estateBoundary: 1.1, stages: 1.3, lots: 1.5 };
  const settings = { ...desktop, mobile, mobileLandscape };

  expect(getDeviceZoomLevels(settings, false, true)).toEqual(mobileLandscape);
  expect(getDeviceZoomLevels(settings, true, false)).toEqual(mobile);
  expect(getDeviceZoomLevels(settings, false, false)?.lots).toBe(3);
  expect(getDeviceZoomLevels({ ...desktop, mobile }, false, true)).toEqual(
    mobile
  );
  expect(getDeviceZoomLevels(desktop, false, true)).toEqual(desktop);
});

describe("camera centering", () => {
  const viewBox = { x: 100, y: 50, width: 1600, height: 900 };

  it("keeps the same framing in a scaled preview and the buyer viewport", () => {
    const center = { x: 950, y: 550 };
    const live = getCameraScroll(
      center,
      viewBox,
      { width: 2400, height: 1350 },
      { width: 1200, height: 800 }
    );
    const preview = getCameraScroll(
      center,
      viewBox,
      { width: 1200, height: 675 },
      { width: 600, height: 400 }
    );

    expect(preview.left).toBeCloseTo(live.left / 2);
    expect(preview.top).toBeCloseTo(live.top / 2);
  });
  it("clamps only the displayed position without changing the saved focal point", () => {
    const center = Object.freeze({ x: 1600, y: 850 });
    const original = getCameraScroll(
      center,
      viewBox,
      { width: 1600, height: 900 },
      { width: 800, height: 450 }
    );

    expect(original).toEqual({ left: 800, top: 450 });
    getCameraScroll(
      center,
      viewBox,
      { width: 1600, height: 900 },
      { width: 1400, height: 850 }
    );
    expect(
      getCameraScroll(
        center,
        viewBox,
        { width: 1600, height: 900 },
        { width: 800, height: 450 }
      )
    ).toEqual(original);
    expect(center).toEqual({ x: 1600, y: 850 });
  });
});

describe("device camera centers", () => {
  const zooms = { estateBoundary: 1, stages: 2, lots: 3 };
  const settings = {
    estateZoomCenter: { x: 100, y: 200 },
    stageZoomCenter: { x: 110, y: 210 },
    zoomLevels: {
      ...zooms,
      mobile: {
        ...zooms,
        estateZoomCenter: { x: 300, y: 400 },
        stageZoomCenter: { x: 310, y: 410 },
      },
      mobileLandscape: {
        ...zooms,
        estateZoomCenter: { x: 500, y: 600 },
        stageZoomCenter: { x: 510, y: 610 },
      },
    },
  };

  it("selects separate estate and stage centers for all three layouts", () => {
    for (const key of ["estateZoomCenter", "stageZoomCenter"] as const) {
      expect(getDeviceZoomCenter(settings, key, false)).toEqual(settings[key]);
      expect(getDeviceZoomCenter(settings, key, true)).toEqual(
        settings.zoomLevels.mobile[key]
      );
      expect(getDeviceZoomCenter(settings, key, false, true)).toEqual(
        settings.zoomLevels.mobileLandscape[key]
      );
    }
  });
  it("preserves legacy centers and lets a device reset without inheriting desktop", () => {
    const legacy = { ...settings, zoomLevels: zooms };

    expect(getDeviceZoomCenter(legacy, "estateZoomCenter", true)).toEqual(
      settings.estateZoomCenter
    );
    const reset = {
      ...settings,
      zoomLevels: {
        ...settings.zoomLevels,
        mobile: { ...zooms, estateZoomCenter: null },
      },
    };

    expect(getDeviceZoomCenter(reset, "estateZoomCenter", true)).toBeNull();
    expect(getDeviceZoomCenter(reset, "estateZoomCenter", false)).toEqual(
      settings.estateZoomCenter
    );
    expect(getDeviceZoomCenter(reset, "estateZoomCenter", false, true)).toEqual(
      settings.zoomLevels.mobileLandscape.estateZoomCenter
    );
  });
});
